/**
 * In-place lock for the Mentor Coach (tier3) surfaces when the server's
 * entitlement payload denies them — e.g. a lapsed tier3 subscriber who kept
 * purchased credits (D3: credits don't unlock mentor engagements or MCS).
 *
 * Built from the mentor workspace primitives plus the existing UpgradeModal,
 * same shape as the Progress tab lock. School-managed users get no checkout
 * CTA; their access follows the school's plan.
 */
import React, { useState } from 'react';
import { useRouter } from 'expo-router';
import { ScrollView, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { WebAppShell } from '@/design-system/components';
import { reviewDesk } from '@/design-system';
import { useAccessPolicy } from '@/hooks/useAccessPolicy';
import { useTranslation } from '@/hooks/useTranslation';
import { ROUTES } from '@/navigation/routes';
import UpgradeModal from '@/components/billing/UpgradeModal';
import { ActionButton, Panel, mentorStyles } from './shared';

type LockedSurface = 'mentor-engagements' | 'mcs-preparation';

export function MentorPlanLock({ surface }: { surface: LockedSurface }) {
  const router = useRouter();
  const { t } = useTranslation();
  const { isB2C } = useAccessPolicy();
  const [upgradeVisible, setUpgradeVisible] = useState(false);
  const isMcs = surface === 'mcs-preparation';

  return (
    <WebAppShell active={surface} contentMaxWidth="100%">
      <ScrollView style={mentorStyles.screen} contentContainerStyle={mentorStyles.content}>
        <Panel testID="mentor_plan_lock">
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <MaterialIcons name="lock-outline" size={24} color={reviewDesk.accent} />
            <Text style={mentorStyles.panelTitle}>
              {t(isMcs ? 'entitlementLocks.mcsTitle' : 'entitlementLocks.mentorTitle')}
            </Text>
          </View>
          <Text style={mentorStyles.bodyText}>
            {t(isMcs ? 'entitlementLocks.mcsBody' : 'entitlementLocks.mentorBody')}
          </Text>
          <View style={mentorStyles.row}>
            {isB2C ? (
              <ActionButton
                testID="mentor_plan_lock_upgrade"
                variant="primary"
                icon="arrow-forward"
                label={t('icfProgress.lockedCta')}
                onPress={() => setUpgradeVisible(true)}
              />
            ) : null}
            <ActionButton
              icon="dashboard"
              label={t('navigation.dashboard')}
              onPress={() => router.replace(ROUTES.TABS_DASHBOARD as never)}
            />
          </View>
        </Panel>
      </ScrollView>
      <UpgradeModal
        visible={upgradeVisible}
        onClose={() => setUpgradeVisible(false)}
        requiredTier="tier3"
        featureKey={isMcs ? 'mcs_preparation' : 'mentor_engagements'}
      />
    </WebAppShell>
  );
}
